import { faCircleCheck, faCircleExclamation, faRotateRight, faScaleBalanced } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/components/ui/shadcn/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useTranslation } from 'react-i18next';
import type {
  MigrationCodPayoutsMetrics,
  MigrationCodPayoutsSample,
  MigrationReconciliationCheck,
  MigrationReconciliationData,
} from '../../adapters/MigrationApiAdapter';
import { useMigrationReconciliation } from '../../hooks/useMigration';

interface MigrationReconciliationPanelProps {
  runId: number;
  formatDate: (date: string | null) => string;
}

const isCodPayoutsMetrics = (metrics: MigrationReconciliationCheck['metrics']): metrics is MigrationCodPayoutsMetrics =>
  'legacy_settlements_total' in metrics && 'warehouse_courier_cod_payments_total' in metrics;

const CodPayoutsCheckCard = ({ check }: { check: MigrationReconciliationCheck }) => {
  const { t } = useTranslation('viewClientModule');

  if (!isCodPayoutsMetrics(check.metrics)) {
    return null;
  }

  const metrics = check.metrics;
  const samples = (check.samples ?? []) as MigrationCodPayoutsSample[];

  const formatAmount = (count: number, total: string) =>
    t('tabs.migration.reconciliation.codPayouts.countAndTotal', { count, total });

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{t('tabs.migration.reconciliation.checks.cod_payouts.title')}</CardTitle>
          <Badge variant={check.passed ? 'default' : 'destructive'} className="gap-1">
            <FontAwesomeIcon icon={check.passed ? faCircleCheck : faCircleExclamation} />
            {check.passed ? t('tabs.migration.reconciliation.passed') : t('tabs.migration.reconciliation.failed')}
          </Badge>
        </div>
        <CardDescription>{check.message}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tabs.migration.reconciliation.metric')}</TableHead>
              <TableHead>{t('tabs.migration.reconciliation.legacy')}</TableHead>
              <TableHead>{t('tabs.migration.reconciliation.warehouse')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">
                {t('tabs.migration.reconciliation.checks.cod_payouts.settlements')}
              </TableCell>
              <TableCell>{formatAmount(metrics.legacy_settlements_count, metrics.legacy_settlements_total)}</TableCell>
              <TableCell>
                {formatAmount(
                  metrics.warehouse_courier_cod_payments_count,
                  metrics.warehouse_courier_cod_payments_total,
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">
                {t('tabs.migration.reconciliation.checks.cod_payouts.payoutLines')}
              </TableCell>
              <TableCell>
                {formatAmount(metrics.legacy_payout_items_count, metrics.legacy_payout_items_total)}
              </TableCell>
              <TableCell>
                {formatAmount(metrics.warehouse_cod_payout_items_count, metrics.warehouse_cod_payout_items_total)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">
                {t('tabs.migration.reconciliation.checks.cod_payouts.internalConsistency')}
              </TableCell>
              <TableCell className="text-muted-foreground">—</TableCell>
              <TableCell>
                {t('tabs.migration.reconciliation.checks.cod_payouts.payoutsSummary', {
                  count: metrics.warehouse_cod_payouts_count,
                  total: metrics.warehouse_cod_payouts_total,
                })}
                {metrics.payouts_with_divergent_total > 0 && (
                  <span className="text-destructive ml-2">
                    (
                    {t('tabs.migration.reconciliation.checks.cod_payouts.divergentPayouts', {
                      count: metrics.payouts_with_divergent_total,
                    })}
                    )
                  </span>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {samples.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">
              {t('tabs.migration.reconciliation.checks.cod_payouts.divergentSamples')}
            </p>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('tabs.migration.reconciliation.checks.cod_payouts.payoutId')}</TableHead>
                  <TableHead>{t('tabs.migration.reconciliation.checks.cod_payouts.payoutTotal')}</TableHead>
                  <TableHead>{t('tabs.migration.reconciliation.checks.cod_payouts.itemsSum')}</TableHead>
                  <TableHead>{t('tabs.migration.reconciliation.checks.cod_payouts.difference')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {samples.map((sample) => (
                  <TableRow key={sample.cod_payout_id}>
                    <TableCell>{sample.cod_payout_id}</TableCell>
                    <TableCell>{sample.total_amount} €</TableCell>
                    <TableCell>{sample.items_sum} €</TableCell>
                    <TableCell className="text-destructive font-medium">{sample.difference} €</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const MigrationReconciliationPanel = ({ runId, formatDate }: MigrationReconciliationPanelProps) => {
  const { t } = useTranslation('viewClientModule');
  const reconciliationMutation = useMigrationReconciliation();

  const handleRunReconciliation = () => {
    reconciliationMutation.mutate({ runId, checks: ['cod_payouts'] });
  };

  const reconciliation = reconciliationMutation.data?.data as MigrationReconciliationData | undefined;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">{t('tabs.migration.reconciliation.description')}</p>
          {reconciliation?.checked_at && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('tabs.migration.reconciliation.checkedAt')}: {formatDate(reconciliation.checked_at)}
            </p>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleRunReconciliation}
          disabled={reconciliationMutation.isPending}
        >
          <FontAwesomeIcon
            icon={reconciliationMutation.isPending ? faRotateRight : faScaleBalanced}
            className={`w-4 h-4 mr-2 ${reconciliationMutation.isPending ? 'animate-spin' : ''}`}
          />
          {reconciliationMutation.isPending
            ? t('tabs.migration.reconciliation.running')
            : t('tabs.migration.reconciliation.run')}
        </Button>
      </div>

      {reconciliationMutation.isError && (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-destructive text-sm">
          <FontAwesomeIcon icon={faCircleExclamation} />
          {t('tabs.migration.reconciliation.error')}
        </div>
      )}

      {!reconciliation && !reconciliationMutation.isPending && !reconciliationMutation.isError && (
        <p className="text-sm text-muted-foreground py-4">{t('tabs.migration.reconciliation.empty')}</p>
      )}

      {reconciliation?.checks.map((check) => {
        if (check.key === 'cod_payouts') {
          return <CodPayoutsCheckCard key={check.key} check={check} />;
        }

        return (
          <Card key={check.key}>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">{check.key}</CardTitle>
                <Badge variant={check.passed ? 'default' : 'destructive'}>
                  {check.passed ? t('tabs.migration.reconciliation.passed') : t('tabs.migration.reconciliation.failed')}
                </Badge>
              </div>
              <CardDescription>{check.message}</CardDescription>
            </CardHeader>
            {Object.keys(check.metrics).length > 0 && (
              <CardContent>
                <Table>
                  <TableBody>
                    {Object.entries(check.metrics).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell className="font-medium">{key}</TableCell>
                        <TableCell>{String(value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
};
